import React, { useEffect, useState } from "react";
import { fetchPaymentReport } from "./financeApi";
import { todayYmd } from "./ScheduleForm";
const money = (value, currency) =>
  `${Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ${currency}`;
const day = (value) =>
  value
    ? new Date(`${value}T12:00:00`).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Не исчерпается в пределах прогноза";
const period = ({ unit, every }) =>
  every === 1
    ? { day: "день", week: "неделю", month: "месяц", year: "год" }[unit]
    : `${every} ${{ day: "дн.", week: "нед.", month: "мес.", year: "лет" }[unit]}`;
export default function PaymentOverview({ refreshKey, onEdit, onTopup }) {
  const [end, setEnd] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [report, setReport] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(true),
    [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setBusy(true);
    setError("");
    fetchPaymentReport(end)
      .then((data) => {
        if (active) setReport(data);
      })
      .catch((e) => {
        if (active) {
          setError(e.message);
          setReport(null);
        }
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [end, refreshKey, retry]);
  return (
    <section className="PaymentOverview" aria-label="Отчёт по платежам">
      <div className="PaymentOverview-period">
        <label>
          Показать платежи до
          <input
            type="date"
            min={todayYmd()}
            value={end}
            onChange={(e) => {
              if (e.target.value) setEnd(e.target.value);
            }}
          />
        </label>
        <span>От сегодня включительно</span>
      </div>
      {error && (
        <div role="alert" className="Finance-banner Finance-banner--error">
          {error}{" "}
          <button
            className="Finance-btn Finance-btn--ghost"
            onClick={() => setRetry((v) => v + 1)}
          >
            Повторить
          </button>
        </div>
      )}
      {busy ? (
        <div className="PaymentOverview-loading" role="status">
          Рассчитываем платежи…
        </div>
      ) : (
        report && (
          <>
            <div className="PaymentOverview-summary">
              <div>
                <p>Обязательно заплатить до даты</p>
                {Object.entries(report.total_required_by_currency).length ? (
                  Object.entries(report.total_required_by_currency).map(
                    ([c, v]) => (
                      <div key={c} className="PaymentOverview-total">
                        {money(v, c)}
                      </div>
                    ),
                  )
                ) : (
                  <div className="PaymentOverview-total">
                    Нет обязательных платежей
                  </div>
                )}
                <small>Суммы пополнений баланса сюда не входят</small>
              </div>
              <div className="PaymentOverview-monthly">
                <p>В среднем за месяц</p>
                {!Object.keys(report.monthly_by_currency).length && (
                  <strong>Нет регулярных расходов</strong>
                )}
                {Object.entries(report.monthly_by_currency).map(([c, v]) => (
                  <strong key={c}>{money(v, c)}</strong>
                ))}
                <small>План на следующие 12 месяцев ÷ 12</small>
              </div>
            </div>
            <h3>
              Обязательные платежи <span>{report.payments.length}</span>
            </h3>
            {report.payments.length ? (
              report.payments.map((p) => (
                <button
                  className="PaymentOverview-row"
                  key={`${p.expense_item_id}-${p.date}`}
                  onClick={() => onEdit(p.expense_item_id)}
                >
                  <div>
                    <strong>{p.name}</strong>
                    {p.group_name && <em className="Finance-groupBadge">{p.group_name}</em>}
                    <small>{day(p.date)}</small>
                  </div>
                  <b>{money(p.amount, p.currency)}</b>
                  <span aria-hidden="true">›</span>
                </button>
              ))
            ) : (
              <p className="PaymentOverview-empty">
                До выбранной даты обязательных платежей нет.
              </p>
            )}
            <h3>Предоплаченные балансы</h3>
            {report.prepaid_accounts.length ? (
              report.prepaid_accounts.map((a) => (
                <article className="PaymentOverview-account" key={a.account_id}>
                  <div className="PaymentOverview-accountHead">
                    <div>
                      <strong>{a.name}</strong>
                      {a.group_names?.map((name) => <em key={name} className="Finance-groupBadge">{name}</em>)}
                      <small>
                        {a.date ? `Исчерпание: ${day(a.date)}` : day(null)}
                      </small>
                      {a.due_in_period && (
                        <span className="PaymentOverview-attention">
                          Пополнить до выбранной даты
                        </span>
                      )}
                    </div>
                    <button
                      className="Finance-btn Finance-btn--ghost"
                      onClick={() => onTopup(a.account_id)}
                    >
                      Пополнить
                    </button>
                  </div>
                  {a.consumption.map((c) => (
                    <button
                      className="PaymentOverview-consumption"
                      key={c.expense_item_id}
                      onClick={() => onEdit(c.expense_item_id)}
                    >
                      <span>{c.name}</span>
                      <span>
                        {c.monthly_rate
                          ? `${money(c.monthly_rate, a.currency)} / месяц · сейчас ${money(c.expense, a.currency)} / день`
                          : `${money(c.expense, a.currency)} / ${period(c.iteration)}`}
                      </span>
                    </button>
                  ))}
                </article>
              ))
            ) : (
              <p className="PaymentOverview-empty">
                Предоплаченных счетов пока нет.
              </p>
            )}
          </>
        )
      )}
    </section>
  );
}
